<template>
  <div>
    <vx-card v-if="board" class="w-full">
      <div class="flex justify-between items-center">
        <h4 class="mb-8">공정분류표 엑셀 업로드</h4>
        <vs-button color="secondary" @click="onClickSave">저장</vs-button>
      </div>

      <!-- 기본정보 -->
      <field-select-module v-model="stdInfo" :board="board" :types="['department', 'com_name', 'rank', 'writer_name', 'date']" />
      <div class="writeGridContainer">
        <div class="writeGridLineItem">
          <div>파일 업로드</div>
          <div class="grid sm:flex items-center gap-2">
            <vs-button class="w-full sm:w-auto" color="secondary" type="border" :href="downloadLink">엑셀 템플릿 다운로드</vs-button>
            <vs-button class="w-full sm:w-auto" color="secondary" @click="onClickExcelUpload"> 작성 엑셀 업로드 </vs-button>
            <input v-show="false" ref="excelInput" type="file" @change="readFile" accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" />
          </div>
        </div>
      </div>

      <!-- 공정분류표 정보 -->
      <div>
        <div class="flex justify-between items-center mt-6 mb-4">
          <h5><i class="dot"></i> 공정분류표 정보 </h5>
          <vs-button color="secondary" type="border" @click="onClickCheckRemove">선택삭제</vs-button>
        </div>
        <div style="max-height: 1070px; overflow-y: auto">
          <table>
            <colgroup>
              <col width="60" />
              <col width="60" />
            </colgroup>
            <thead>
            <th><vs-checkbox :value="0 < excelData.length && excelData.length === checkList.length" @click.native="checkAll" class="check" /></th>
            <th v-for="th in tableHeader">{{ th }}</th>
            <th>수정/삭제</th>
            </thead>
            <tbody>
            <tr v-for="(excel, rowIndex) in excelData" :key="`excelData_${rowIndex}`">
              <td><vs-checkbox :vs-value="rowIndex" v-model="checkList" class="check" /></td>
              <td v-for="(columnName, columnIndex) in tableHeader.length" :key="`${rowIndex}_${excel[0]}_${columnIndex}`">
                <div v-if="editExcelIndex === rowIndex" class="p-1">
                  <fragment v-if="columnIndex === 0">
                    {{editExcelData.values[columnIndex]}}
                  </fragment>
                  <fragment v-else-if="columnIndex === 1">
                    <vs-select class="w-full" autocomplete v-model="editExcelData.values[columnIndex]" >
                      <vs-select-item v-for="item in ecoFlow.items" :text="item.label" :value="item.value"/>
                    </vs-select>
                  </fragment>
                  <fragment v-else-if="columnIndex === 2">
                    <field-selector class="w-full text-left" v-model="editExcelData.values[columnIndex].id" @select="(field) => onSelectField(rowIndex, columnIndex, field)"/>
                  </fragment>
                  <fragment v-else-if="columnIndex > 0">
                    <ml-input v-model="editExcelData.values[columnIndex]" class="w-full" />
                  </fragment>
                </div>
                <div v-else>
                  <p v-if="columnIndex === 1">{{ ecoFlow.getLabel(excel.values[columnIndex]) || "-" }}</p>
                  <p v-else-if="columnIndex === 2">{{ excel.values[columnIndex].field_name || "-" }}</p>
                  <p v-else>{{ excel.values[columnIndex] || "-" }}</p>
                  <p v-if="excel.errorMSG" class="errMsg">{{ excel.errorMSG[columnIndex] || ""}}</p>
                </div>
              </td>
              <td>
                <div class="flex justify-center gap-3">
                  <!-- 저장/닫기 -->
                  <fragment v-if="editExcelIndex === rowIndex">
                    <button @click="onClickFinishExcelData(rowIndex)">
                      <feather-icon icon="CheckIcon" svgClasses="h-5 w-5" />
                    </button>
                    <button @click="onClickCancelExcelData(rowIndex)">
                      <feather-icon icon="XIcon" svgClasses="h-5 w-5" />
                    </button>
                  </fragment>
                  <!-- 수정/삭제 -->
                  <fragment v-else>
                    <button @click="onClickEditExcelData(rowIndex)">
                      <feather-icon icon="Edit2Icon" svgClasses="h-5 w-5" />
                    </button>
                    <button @click="onClickRemoveExcelData(rowIndex)">
                      <feather-icon icon="Trash2Icon" svgClasses="h-5 w-5" />
                    </button>
                  </fragment>
                </div>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>
    </vx-card>
  </div>
</template>

<script>
import FieldSelectModule from "@/views/modules/FieldSelectModule";
import FieldSelector from "@/components/selector/FieldSelector";
import CompanySelector from "@/components/selector/CompanySelector";
import DateSelector from "@/components/selector/DateSelector";
import config from "/public/config";
import moment from "moment";
import HttpRequest from "@/util/HttpRequest";
import ExcelJS from "exceljs";
import Select736Button from "@/views/boards/single-page/736/Select736Button.vue";
import ecoFlow from "@/views/boards/single-page/977/ecoFlow";

export default {
  components: {
    Select736Button,
    FieldSelectModule,
    FieldSelector,
    CompanySelector,
    DateSelector,
  },
  props: {
    board: Object,
  },
  data() {
    return {
      downloadLink: `${config.BASE_API_URL}/storage/app/media/%EB%8F%99%EA%B5%AD%EC%A0%9C%EA%B0%95_%ED%99%98%EA%B2%BD%EC%8B%9C%EC%84%A4_%EB%8C%80%EB%9F%89%EB%93%B1%EB%A1%9D.xlsx`,
      tableHeader: ['No.', '공정명', '사업장', '공정번호', '관리번호', '시설명', '용량', '시설번호'],
      fields: [],
      stdInfo: {},

      // 엑셀 데이터 관련
      // 데이터구조 :
      //  [
      //    {
      //       values: [1, 'field_name', 'data1', 'data2', ...],
      //       errorMSG: ["", "", "", "", ...]
      //    }
      //  ]
      editExcelIndex: null,
      editExcelData: [],
      excelData: [],
      checkList: [],
      select736: {},
    };
  },
  computed: {
    projectId() {
      return parseInt(this.$route.params.proj_id);
    },
    boardId() {
      return parseInt(this.$route.params.boardId);
    },
    pageTitle() {
      return this.$store.state.menu.pageTitle;
    },
    ecoFlow() {
      return ecoFlow;
    },
  },
  async mounted() {
    await this.loadFieldListAll();
  },
  methods: {
    async loadFieldListAll() {
      this.loading(true);
      const loadFieldList = (page) => {
        this.$store.dispatch("project/LOAD_FIELD_LIST", {
          projectId: this.projectId,
          page,
          perPage: 50,
        }).then((response) => {
          const data = response.data;
          this.fields.push(...data)
          if (response.last_page > page) {
            loadFieldList(page + 1);
          } else {
            this.loading(false);
          }
        });
      }
      await loadFieldList(1)
    },

    findFieldByFieldName(fieldName) {
      return this.fields.find((field) => field.field_name === fieldName);
    },

    onClickExcelUpload() {
      const excelInput = this.$refs.excelInput;
      excelInput.value = null;
      excelInput.click();
    },
    // 엑셀데이터 파싱
    readFile(event) {
      const workbook = new ExcelJS.Workbook();
      const file = event.target.files[0];
      const reader = new FileReader();
      reader.readAsArrayBuffer(file);
      reader.onload = (e) => {
        const buffer = reader.result;
        workbook.xlsx.load(buffer).then((workbook) => {
          workbook.eachSheet((sheet) => {
            sheet.eachRow((row, rowNumber) => {

              // data form 가공
              const cells = row.values.map((cell) => {
                if (cell.richText) return cell.richText.map((rt) => rt.text).join("");
                if (cell instanceof Date) return moment(cell).format("YYYY-MM-DD");
                return cell;
              });
              this.getDataInSheet(sheet, rowNumber, cells)
            });
          });
          //
          // this.excelData = data;
        });
      };
    },

    // rowNumber: 2부터 시작
    // cells: [empty, col1, col2, col3, ...]
    async getDataInSheet(sheet, rowNumber, cells=[]) {
      if (rowNumber > 2) {
        const error = [];

        // 공정명
        const ecoFlowLabel = ecoFlow.getLabel(cells[1])
        const ecoFlowValue = ecoFlow.getValue(ecoFlowLabel || cells[1])
        cells[1] = ecoFlowValue;
        if (!ecoFlowValue) {
          error[1] = "입력된 데이터가 검색되지 않습니다.";
        }

        // 사업장
        const field = this.findFieldByFieldName(cells[2]);
        cells[0] = this.excelData.length + 1;
        if (field) {
          cells[2] = {
            id: field.id,
            field_name: field.field_name,
          };
        } else {
          cells[2] = {
            id: 0,
            field_name: cells[2],
          };
          error[2] = "입력된 데이터가 검색되지 않습니다.";
        }
        this.excelData.push({
          values: cells,
          errorMSG: error,
        });
      }
    },


    setMoment(date) {
      let _date = date;
      if (typeof _date === "number") _date = `${_date}`;
      if (typeof _date === "string" && !_date.includes("-")) _date = `${_date.slice(0, 4)}-${_date.slice(4, 6)}-${_date.slice(6, 8)}`;
      return moment(_date).add(1, "days").format("YYYY-MM-DD");
    },
    setPhone(data) {
      let _data = `${data}`;
      if (typeof data === "number" && _data.length === 10 && _data.charAt(0) !== "0") _data = `0${_data}`;
      return _data.replace(/^(\d{3})(\d{4})(\d{4})/, "$1-$2-$3");
    },
    onClickEditExcelData(index) {
      this.editExcelIndex = index;
      this.editExcelData = JSON.parse(JSON.stringify(this.excelData[index]));
    },
    onClickFinishExcelData(index) {
      this.$set(this.excelData, index, this.editExcelData);
      this.onClickCancelExcelData();
    },
    onClickCancelExcelData(index) {
      this.editExcelData = [];
      this.editExcelIndex = null;
    },
    onClickRemoveExcelData(index) {
      this.excelData.splice(index, 1);
    },
    checkAll() {
      if (0 < this.excelData.length && this.excelData.length === this.checkList.length) this.checkList = [];
      else this.checkList = this.excelData.map((_, i) => i);
    },
    onClickCheckRemove() {
      if (this.checkList.length < 1) return;
      this.excelData = this.excelData.filter((_, i) => !this.checkList.includes(i));
      this.excelData.forEach((_, i) => {
        _.values[0] = i + 1;
      });
      this.checkList = [];
    },
    onClickSave() {
      // 중복 데이터 제거
      const forms = [...new Set(this.excelData.map((ed) => {
        const data = JSON.parse(JSON.stringify(ed.values))
        const form = {
          stdInfo: {
            ...this.stdInfo,
            field_id: data[2].id,
            field_name: data[2].field_name,
          },
          시설정보: {
            공정명: data[1],
            공정번호: data[3],
            관리번호: data[4],
            시설명: data[5],
            용량: data[6],
            시설번호: data[7],
          },
        }
        return JSON.stringify(form)
      }))].map((d) => JSON.parse(d));

      // 엑셀 데이터가 없을때 에러
      if (this.excelData.length < 1) {
        this.customAlert("공정분류표 정보 데이터가 없습니다");
        return;
      }

      // 에러가 있는 필드가 있을 경우,
      if (this.excelData.filter((row) => row.errorMSG.filter((msg) => !!msg).length > 0).length > 0) {
        this.customAlert("공정분류표 정보에 올바르지 않은 데이터가 있습니다");
        return;
      }

      // 중복 데이터가 있을 경우,
      // if (forms.length !== this.excelData.length) {
      //   this.customAlert("중복된 데이터가 있습니다");
      //   return;
      // }


      this.writeOnClickSaveMultiple({
        posts: forms,
        callback: (result, response) => {
          if (result) {
            const excelData = JSON.parse(JSON.stringify(this.excelData));
            this.excelData = [];
            this.checkList = [];
            let msg = "저장되었습니다";
            if (forms.length != excelData.length) {
              msg += "<br>중복된 데이터는 제거 되었습니다.";
              msg += `<br><br><span style="font-weight: bold">중복된 데이터: ${(excelData.length - forms.length)}건</span>`;
            }
            this.customAlert(msg, "공정분류표 엑셀 업로드");
          }
        },
      });
    },
    writeOnClickSaveMultiple(forms) {
      const boardId = this.boardId;
      this.loading(true);
      const date = new Date();
      const header = {...this.header, full: date.format("yyyy-MM-dd")};
      const posts = forms.posts.map((form) => {
        const content = {
          header,
          body: {
            ...form
          },
        };
        return {
          title: this.pageTitle,
          content: JSON.stringify(content),
          projectId: this.projectId,
          company_member_join: true,
        }
      });
      HttpRequest.put(`/api/v1/board/post/${boardId}/multiple`, { posts })
        .then((response) => {
          if (forms.callback) {
            forms.callback(true, response);
          }

          this.loading(false);
        })
        .catch(({ response }) => {
          if (forms.callback) {
            forms.callback(false, response);
          }
          this.loading(false);
        });
    },
    onSelectField (rowIndex, columnIndex, field) {
      this.editExcelData.values[columnIndex] = {
        id: field.id,
        field_name: field.field_name,
      };
      this.editExcelData.errorMSG[columnIndex] = field.id ? "" : "입력된 데이터가 검색되지 않습니다.";
    },

    customAlert(msg, title) {
      const customAlert = new CustomEvent("customAlert", {
        detail: {
          title: !!title ? title : "오류가 발생했습니다",
          message: msg,
        },
      });
      window.dispatchEvent(customAlert);
    },
    loading(open) {
      const loadingDialog = new CustomEvent("loadingDialog", {
        detail: {
          open,
        },
      });
      window.dispatchEvent(loadingDialog);
    },
  },
};
</script>

<style scoped lang="scss">
table {
  table-layout: fixed;
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    text-align: center;
    padding: 0;
  }

  th {
    font-weight: 700;
    font-size: 14px;
    line-height: 17px;

    background: #fafafa;
    border-top: 1px solid #cdcdcd;
    border-bottom: 1px solid #cdcdcd;
    padding: 16px 0;
  }

  td {
    font-weight: 400;
    font-size: 14px;
    line-height: 17px;

    border-bottom: 1px solid #cdcdcd;
    padding: 16px 0;
  }

  button {
    width: 32px;
    height: 32px;

    display: flex;
    align-items: center;
    justify-content: center;

    color: #7e72f2;
    background: none;
    border: 1px solid #7e72f2;
    border-radius: 4px;
    cursor: pointer;

    span {
      margin: 0;
    }
  }

  .check {
    justify-content: center !important;

    ::v-deep {
      span {
        margin: 0;
      }
    }
  }
}

.errMsg {
  font-weight: 400;
  font-size: 12px;
  line-height: 17px;
  text-align: center;
  color: #ea5455;
  margin-top: 2px;
}
</style>
